import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';
import { default as qs } from 'qs';

const initialState: ProductState = {
  all: [],
  viewing: undefined,
};

const getters: GetterTree<ProductState, RootState> = {
  all(state: ProductState): Product[] {
    return state.all;
  },
  viewing(state: ProductState): Product | undefined {
    return state.viewing;
  },
};

const mutations: MutationTree<ProductState> = {
  RESET(state: ProductState) {
    state.all = [];
    state.viewing = undefined;
  },
  ADD_MODEL(state: ProductState, payload: Product) {
    state.all.push(payload);
  },
  SET_MODELS(state: ProductState, payload: Product[]) {
    state.all = payload;
  },
  ADD_MODELS(state: ProductState, payload: Product[]) {
    state.all = [...state.all, ...payload];
  },
  UPDATE_MODEL(state: ProductState, payload: Product) {
    const index = state.all.findIndex((model: Product) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      state.all[index] = payload;
    }
  },
  REMOVE_MODEL(state: ProductState, payload: Product) {
    state.all = state.all.filter((model: Product) => model.id !== payload.id);
  },
  SET_VIEWING(state: ProductState, payload: Product) {
    state.viewing = payload;
  },
};

const resource: string = 'products';

const actions: ActionTree<ProductState, RootState> = {
  clearViewing({ commit }) {
    commit('SET_VIEWING', undefined);
  },
  create({ commit, rootState }, payload: { product: Product; workspaceId: number }) {
    return rootState.api
      .post(`${resource}`, payload.product, { withCredentials: true, headers: { common: { 'x-workspace-id': payload.workspaceId } } })
      .then((response: { data: Product }) => {
        commit('ADD_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index({ commit, rootState }, payload?: { query?: IndexQuery<Product>; workspaceId: number }) {
    const queryString = qs.stringify(payload?.query, { encode: false });
    return rootState.api
      .get(`${resource}?${queryString}`, { withCredentials: true, headers: { common: { 'x-workspace-id': payload?.workspaceId } } })
      .then((response: { data: Product }) => {
        if (payload && payload.query?.hasOwnProperty('after')) {
          commit('ADD_MODELS', response.data);
        } else {
          commit('SET_MODELS', response.data);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  externalProducts({ commit, rootState }, payload: { query?: { after?: Array<number | string>; ids?: number[] }; workspaceId: number }) {
    return rootState.api
      .get(`external-products`, { withCredentials: true, params: payload?.query, headers: { common: { 'x-workspace-id': payload?.workspaceId } } })
      .then((response: { data: Product }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, payload: { id: number; workspaceId: number }) {
    return rootState.api
      .get(`${resource}/${payload.id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': payload.workspaceId } } })
      .then((response: { data: Product }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, payload: { product: Product; workspaceId: number }) {
    return rootState.api
      .put(`${resource}/${payload.product.id}`, payload.product, { withCredentials: true, headers: { common: { 'x-workspace-id': payload.workspaceId } } })
      .then((response: { data: Product }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, payload: { id: number; workspaceId: number }) {
    return rootState.api
      .delete(`${resource}/${payload.id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': payload.workspaceId } } })
      .then((response: { data: Product }) => {
        commit('REMOVE_MODEL', payload);

        return Promise.resolve(payload);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
